import React, { useCallback, useContext, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { GoogleMap, Marker, DrawingManager, Polygon, InfoWindow } from '@react-google-maps/api';
import { MapContext } from '../../Contexts/MapContext';
import YellowMapPinIcon from '../../Assets/img/icon-map-pin-yellow.png';
import BlueMapPinIcon from '../../Assets/img/icon-map-pin-blue.png';
import SubscribedMapPinIcon from '../../Assets/img/icon-map-pin-subscribed.png';
import CurrentLocationPinIcon from '../../Assets/img/icon-current-location-pin.svg';
import InfoIcon from '../../Assets/img/icon-info.svg';
import DeleteIcon from '../../Assets/img/icon-red-trash.svg';
import EditIcon from '../../Assets/img/icon-edit-grey.svg';
import { MapUtils } from '../MapUtils';
import { AOIOptions } from '../AOIOptions';
import { UserContext } from '../../Contexts/UserContext';
import UsecaseLinks from './UsecaseLinks';
import RenderTag from './RenderTag';
import { AOIContext } from '../../Contexts/AOIContext';
import { hasPermission } from '../../Utils/PermissionUtils';
import { Permission } from '../../Utils/Permission';
import Moment from 'react-moment';
import { Link } from 'react-router-dom';

const MarkerIcon = { url: YellowMapPinIcon, scaledSize: { width: 32, height: 52 } };
const SelectedIcon = { url: BlueMapPinIcon, scaledSize: { width: 32, height: 52 } };
const SubscribedIcon = { url: SubscribedMapPinIcon, scaledSize: { width: 32, height: 52 } };
const CurrentLocationIcon = { url: CurrentLocationPinIcon, scaledSize: { width: 32, height: 52 }}

const AOIMap = (props) => {
    const navigate = useNavigate();
    const {isMapLoaded, loadError, mapRef, drawingManagerRef, center, locationPoint, userAOIs,
        deleteShapes,
        selectedAOI,
        savePolygonPaths,
        aoi, aoiMode,
        showAOIPopover,setShowAOIPopover,
        zoomToFit, setZoomToFit,
        viewAOIDetails, 
        currentLocation, showCurrentLocation,
        discardAOI
    } = useContext(MapContext);
    const { serviceArea, deleteAOI, hasAOIDeletePermission, editAOI, hasAOIEditPermission } = useContext(AOIContext);
    const {user, domainConfig, siteCache, selectedOrgId} = useContext(UserContext);
    const [markerMap, setMarkerMap] = useState({});
    const [polygonMap, setPolygonMap] = useState({});
    const [infoOpen, setInfoOpen] = useState(false);    
    const [saveAOIMarker, setSaveAOIMarker] = useState(null);
    const [currentLocationMarker, setCurrentLocationMarker] = useState(null);
    const isZoomControlRequired = user.userPreferences?.ZoomControl ? user.userPreferences.ZoomControl : false;
    const [mapOptions, setMapOptions] = useState({...AOIOptions.mapOptions, zoomControl : isZoomControlRequired});
    const strokeColor = AOIOptions.colorCodes[user.userPreferences?.AOIBorderColour]|| AOIOptions.userAOIoptions.strokeColor;
    const [aoiOptions, setAoiOptions] = useState({...AOIOptions.userAOIoptions, strokeColor: strokeColor});
    const isDrawingToolBoxRequired = user.userPreferences?.FreeStyleDrawing ? user.userPreferences.FreeStyleDrawing : true;
    const isRegisteredOnly = user.isRegistered && !(user.isDA || user.isCustomerService || user.isAdmin || user.isSuperAdmin);

    const [canCreateAoi, setCanCreateAOi] = useState(false);
    const [canEditShape, setCanEditShape] = useState(false);
    
    useEffect(() => {
        setCanCreateAOi(hasPermission(user, Permission.AoiCreate, selectedOrgId));
        setCanEditShape(hasPermission(user, Permission.AoiUpdateGeometry, selectedOrgId));
    }, [user, selectedOrgId]);

    const onMapLoad = useCallback((map) => {
        // Store the Map reference in mapRef
        mapRef.current = map;
    }, [serviceArea]);

    useEffect(()=> {
        if(mapRef && mapRef.current && selectedAOI)
            aoiClickHandler(null, selectedAOI);
    }, [selectedAOI, mapRef, mapRef.current]);

    useEffect (()=>{
        if(mapRef && mapRef.current && userAOIs) {
            mapRef.current.data.setStyle(aoiOptions);
        }
    },[aoiOptions])

    useEffect(() => {
        if(mapRef && mapRef.current && userAOIs) {
            mapRef.current.data.forEach(function(feature){
                mapRef.current.data.remove(feature);
            });
            userAOIs.map((aoi) => {
                const constGeojson = {"type": "Feature", "id": aoi.tag, "properties":{}, "geometry": aoi.geometry};
                aoi.geojson = mapRef.current.data.addGeoJson(constGeojson);
            });
            mapRef.current.data.setStyle(aoiOptions);
        }
    }, [userAOIs, mapRef, mapRef?.current]);

    // We have to create a mapping of our places to actual Marker objects
    const markerLoadHandler = (marker, aoi) => {
        return setMarkerMap(prevState => {
            return { ...prevState, [aoi.aoiId]: marker };
        });
    };

    const aoiClickHandler = (event, aoi) => {
        navigate(`/portfolio/map/aoi/${aoi.aoiId}`, {replace: true});

        // For some unknown reason or maybe because of some race conditions, the info window is getting opened only when there is a slight delay in setting the state.
        // That's why, have put a delay of 10 milli seconds to open the info window. 
        setTimeout(() => {
            setInfoOpen(false);
            setInfoOpen(true);
        }, 50);

        mapRef.current.panTo(MapUtils.getCenterForGeometry(aoi.geometry));
        if(!MapUtils.areAllBoundsInsideMap(aoi.geometry, mapRef) || zoomToFit == true) {
            setTimeout(() => {
                MapUtils.fitBoundsSelectectedAOI(aoi, mapRef);
            }, 100);
            setZoomToFit(false);
        }
    };

    const handleEditAOI = (selectedAOI, marker) => {
        MapUtils.fitBoundsSelectectedAOI(selectedAOI, mapRef);
        setInfoOpen(false);
        console.log(polygonMap);
        const polygon = polygonMap[[selectedAOI.aoiId]];
        console.log(polygon);
        props.editAOI(selectedAOI, polygon);
        mapRef.current.data.overrideStyle(mapRef.current.data.getFeatureById(selectedAOI.name), {editable: true});
    }

    const onDrawingManagerLoad = useCallback((drawingManager) => {
        drawingManagerRef.current = drawingManager;
        deleteShapes();
    }, []);

    const handleInfoWindowClose = () => {
        setInfoOpen(false);
    }

    const onZoomChange = () => {
        if(mapRef.current && mapRef.current.zoom){
            setAoiOptions(
                {
                    ...aoiOptions,
                    strokeWeight: AOIOptions.zoomAssociatedStrokeWeight[mapRef.current.zoom] || aoiOptions.strokeWeight
                }
            )
        }
    };

    const isSubscribed = (subscribedUsecases) => {
        if(subscribedUsecases == null)
            return false;
        const usecaseNames = domainConfig.usecases.filter(u => u.isEnabled === true).map(u => u.name);
        return subscribedUsecases.some(u=>u.usecase.type == 'aoi' && usecaseNames.indexOf(u.usecase.name) >= 0);
    };

    const renderMap = () => {
        return (
            <div className="map-container aoi-map">
               {
                    <GoogleMap
                        center = {center}
                        zoom={siteCache?.mapZoom ?? 3}
                        mapContainerStyle={AOIOptions.mapContainerStyle}
                        options = {mapOptions}
                        onLoad = {onMapLoad}
                        onBoundsChanged={e => props.mapChanged()}
                        onZoomChanged = {onZoomChange}
                    >
                        { /** Drawing Manager Widget */
                            canCreateAoi && 
                            isDrawingToolBoxRequired && 
                            <DrawingManager
                                onLoad = {onDrawingManagerLoad}
                                onOverlayComplete = {props.onDrawingComplete}
                                options = {AOIOptions.defaultDrawingManagerOptions}
                            />
                        }
                        
                        { /* Search Location Marker */
                            locationPoint && <Marker position={locationPoint} /> 
                        }

                        { /* Current Location Marker */
                            showCurrentLocation && currentLocation && 
                            <Marker icon={CurrentLocationIcon} zIndex={1000}
                                position={currentLocation} 
                                onLoad={(marker)=>{setCurrentLocationMarker(marker)}}
                            /> 
                        }

                        {
                            /* InfoWindow in Current Location */
                            showCurrentLocation && currentLocationMarker &&
                            <InfoWindow anchor={currentLocationMarker}>
                                <div>Your Current Location</div>
                            </InfoWindow>
                        }

                        { /* Saved AOIs Marker */      
                            userAOIs.map((aoi) => (
                                <Marker 
                                    icon={selectedAOI?.aoiId == aoi.aoiId && infoOpen ? SelectedIcon : (isSubscribed(aoi.subscribed_usecases) ? SubscribedIcon :MarkerIcon)}
                                    key={aoi.aoiId}
                                    position={MapUtils.getCenterForGeometry(aoi.geometry)} 
                                    onLoad={marker => markerLoadHandler(marker, aoi)} 
                                    onClick={event => aoiClickHandler(event, aoi)}
                                />
                            ))
                        }

                        { /* InfoWindow in saved AOIs */
                            infoOpen && selectedAOI && (
                                <InfoWindow
                                    anchor={markerMap[selectedAOI.aoiId]}
                                    onCloseClick={handleInfoWindowClose}
                                >
                                    <div className="aoi-popover-div">
                                        <div className="popover-content">
                                            <div className="aoi-info">
                                                <div className="info-row">
                                                    <h4>AOI Name</h4>
                                                    <RenderTag tag={selectedAOI.name} />
                                                </div>
                                                <div className="info-row">
                                                    <h4>Area</h4>
                                                    <h3>{MapUtils.transformArea(selectedAOI.area)}</h3>
                                                </div>
                                                <div className="info-row">
                                                    <h4>Date Created/Modified</h4>
                                                    <h3><Moment date={selectedAOI.updatedDate} format={"Do MMMM YYYY"}></Moment></h3>
                                                </div>
                                            </div>
                                            <div className="aoi-actions">
                                                <Link to={`/timeline/${selectedAOI.aoiId}`} className='btn-link'><i className='timeline-icon'></i>Timeline</Link>

                                                {/* <a className="btn-link" onClick={()=>handleEditAOI(selectedAOI, markerMap[selectedAOI.aoiId])}><i className='edit'></i>Edit Shape</a> */}
                                                <a className="btn-link" onClick={() => MapUtils.fitBoundsSelectectedAOI(selectedAOI, mapRef)}><i className='zoom'></i>Zoom in</a>
                                                <div className='icon-links' style={{display:'flex'}}>
                                                    { hasAOIEditPermission && <a><img src={EditIcon} className='edit-aoi-icon' title="Edit AOI" onClick={()=>editAOI(selectedAOI.aoiId)}/></a>}
                                                    { hasAOIDeletePermission && <a><img src={DeleteIcon} className='delete-aoi-icon' title="Delete AOI" onClick={()=>deleteAOI(selectedAOI.aoiId)}/></a>}
                                                </div>
                                                
                                                <UsecaseLinks aoi={selectedAOI} state={{dashboardView:'map'}} type="aoi"></UsecaseLinks>
                                            </div>
                                        </div>
                                    </div>
                                </InfoWindow>
                            )
                        }
                       
                        { /* Create New AOI Polygon */
                            (savePolygonPaths.length > 0 && aoiMode == 'create') &&
                            <Polygon
                                onLoad = {props.onCreateAOIPolygonLoad}
                                paths= {savePolygonPaths}
                                options= {AOIOptions.createAOIPolygonoptions}
                            />
                        }
                        { /* Save New AOI Marker */
                            showAOIPopover &&
                            <Marker 
                                icon={MarkerIcon}
                                position={MapUtils.getCenterFromCoordinates(savePolygonPaths)} 
                                onLoad={(marker)=>{setSaveAOIMarker(marker)}}
                            />
                        }
                        { /* Save AOI InfoWindow */
                            showAOIPopover &&
                            <InfoWindow
                                anchor={saveAOIMarker}
                                onCloseClick={()=>setShowAOIPopover(false)}
                                onLoad={()=>setInfoOpen(false)}
                            >
                                <div className='aoi-popover-div'>
                                    <div className="popover-content">
                                        <div className="new-aoi">
                                            <div className="form-group">
                                                <label className="form-label">AOI Name</label>
                                                <input type="text" name="AOIName" className="form-control" value={aoi.tag} 
                                                    onChange={(e) => props.handleAOINameInputChange(e)} 
                                                    onFocus={(e) => e.target.select()}/>
                                                {
                                                    aoiMode == 'create' &&
                                                    <div className="info-notes"><img src={InfoIcon} alt="" /> 
                                                        This name is auto-generated. You can modify as needed.
                                                    </div>
                                                }
                                            </div>
                                            <div className="cta">
                                                <a className="btn-cancel" onClick={discardAOI}>Cancel</a>
                                                <a className="btn-save" onClick={() => props.saveAOI()}>Save &amp; Continue</a>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </InfoWindow>
                        }
                    </GoogleMap>
               } 
            </div>
        )
    }

    if (loadError) {
        return <div className='map-load-text'>Map cannot be loaded right now, sorry.</div>
    }
    
    return isMapLoaded ? renderMap() : <div className='map-load-text'><span>Loading Map...</span></div>
}

export default React.memo(AOIMap);